import { createSelector } from 'reselect';
import { pipe } from 'fp-ts/function';
import { array, nonEmptyArray, record, string } from 'fp-ts';
import { Amount, OrderItem, ProductOrderItem } from '@mews-commander/types';
import { AccountingState } from '@mews-commander/data-access';
import { composeProps, createPropsExtender } from '@mews/framework';
import { findOriginalAmount } from '@mews-commander/ui';
import { getItemByCommonPropertyValue } from '@mews/fp';
import { getContainer } from '@mews-commander/legacy-app-instance';
import { areAmountsEqual } from '@mews-commander/utils';
import { isEnterpriseMultiTaxEnvironment } from '@mews-commander/legacy-entities/src/ducks/enterprises';
import { getEnterpriseIdFromServiceId } from '@mews-commander/legacy-entities/src/ducks/services';
import {
    getCommonReservationsDefaultProductOrderItemsAverageAmount,
    getCommonReservationsDefaultProductOrderItems,
} from '../ducks/orderItems';

const isMultiTaxEnvironment = composeProps(
    isEnterpriseMultiTaxEnvironment,
    createPropsExtender('enterpriseId', getEnterpriseIdFromServiceId)
);

export const getAverageNightRateInitialValues = createSelector(
    getCommonReservationsDefaultProductOrderItemsAverageAmount,
    isMultiTaxEnvironment,
    getContainer,
    (averageAmount, isMultiTax, container) => {
        if (averageAmount) {
            const { appLocalizationComponent } = container();
            const { grossValue, netValue, currency } = findOriginalAmount(averageAmount) ?? {};
            const value = isMultiTax ? netValue : grossValue;
            return {
                currency,
                value:
                    (value != null &&
                        !!currency &&
                        appLocalizationComponent.localizeCurrencyValue({ value, currency }, { showSymbol: false })) ||
                    null,
            };
        }

        return {
            currency: null,
            value: null,
        };
    }
);

const createOrderItemMatcher =
    (startUtc: string | null, consumedUtc: string, totalAmount: Amount) => (orderItem: OrderItem) =>
        startUtc === orderItem.startUtc &&
        consumedUtc === orderItem.consumedUtc &&
        areAmountsEqual(totalAmount, orderItem.totalAmount);

const getOrderItemsCommonPerNightPrices = (productOrderItems: ProductOrderItem[], isMultiTax: boolean) =>
    pipe(
        productOrderItems,
        nonEmptyArray.groupBy(({ startUtc }) => startUtc ?? ''),
        record.collect(string.Ord)((_, orderItems) => {
            const container = getContainer();
            const { appLocalizationComponent } = container();
            const [firstOrderItemInGroup, ...otherOrderItems] = orderItems;
            const { startUtc, consumedUtc, totalAmount, proRataStartUtc, proRataEndUtc } = firstOrderItemInGroup;
            const originalAmount = findOriginalAmount(totalAmount);

            const matcher = createOrderItemMatcher(startUtc, consumedUtc, originalAmount as Amount);
            const hasMatch = otherOrderItems.every(matcher);
            let localizedValue = '';
            if (hasMatch && originalAmount) {
                const { grossValue, netValue, currency } = originalAmount;
                localizedValue = appLocalizationComponent.localizeCurrencyValue(
                    {
                        value: isMultiTax ? netValue ?? 0 : grossValue ?? 0,
                        currency,
                    },
                    {
                        showSymbol: false,
                    }
                );
            }

            const enabled = orderItems.every(
                ({ accountingState }) =>
                    accountingState === AccountingState.Open || accountingState === AccountingState.Inactive
            );

            return {
                productOrderIds: orderItems.map(({ id }) => id),
                consumedUtc,
                startUtc,
                value: localizedValue,
                enabled,
                proRataStartUtc,
                proRataEndUtc,
            };
        })
    );

const getCommonAmountsCurrency = getItemByCommonPropertyValue<Amount, 'currency'>('currency');
const getOrderIdemsCommonCurrency = (orderItems: ProductOrderItem[]) => {
    if (orderItems.length === 0) return null;

    return pipe(
        orderItems,
        array.map((orderItem) => findOriginalAmount(orderItem.totalAmount) as Amount),
        getCommonAmountsCurrency
    );
};

export const getPerTimeUnitRateInitialValues = createSelector(
    getCommonReservationsDefaultProductOrderItems,
    isMultiTaxEnvironment,
    (productOrderItems, isMultiTax) => ({
        currency: getOrderIdemsCommonCurrency(productOrderItems),
        perNight: getOrderItemsCommonPerNightPrices(productOrderItems, isMultiTax),
    })
);
